import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateCAGR } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const CAGRCalculator = () => {
  const navigate = useNavigate();
  const [cagrStart, setCagrStart] = useState(10000);
  const [cagrEnd, setCagrEnd] = useState(20000);
  const [cagrYears, setCagrYears] = useState(5);

  const cagrResult = useMemo(
    () => calculateCAGR(cagrStart, cagrEnd, cagrYears),
    [cagrStart, cagrEnd, cagrYears]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                CAGR Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-emerald-100 p-3">
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        CAGR Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Compound Annual Growth Rate
                      </p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Initial Value"
                      value={cagrStart}
                      onChange={setCagrStart}
                      min={100}
                      max={10000000}
                      step={100}
                      prefix="₹"
                      colorClass="text-primary"
                      hideSlider
                    />

                    <CompactCalculatorRow
                      label="Final Value"
                      value={cagrEnd}
                      onChange={setCagrEnd}
                      min={100}
                      max={100000000}
                      step={100}
                      prefix="₹"
                      colorClass="text-primary"
                      hideSlider
                    />

                    <CompactCalculatorRow
                      label="Duration"
                      value={cagrYears}
                      onChange={setCagrYears}
                      min={1}
                      max={50}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-blue-600"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="mb-8 text-center">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      CAGR Rate
                    </p>
                    <h2 className="text-4xl font-black text-emerald-600">
                      {cagrResult}%
                    </h2>
                  </div>
                  <div className="space-y-2 rounded-xl border border-slate-100 bg-white p-6 text-center shadow-sm">
                    <p className="font-medium text-slate-500">
                      Your investment has grown at an average annual rate of
                    </p>
                    <p className="text-2xl font-black text-secondary">
                      {cagrResult}%
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
